import React, { useEffect, useRef, useState } from 'react'
import type { HeadingLevel } from 'src/typings/headingTags'
import { useEventPromoView } from 'src/hooks/usePromoView'
import { useInView } from 'react-intersection-observer'
import { useErrorComponentEvent } from 'src/sdk/analytics/hooks/useErrorComponentEvent'
import { DEFAULT_DATA_NAVIGATION_CATEGORY } from 'src/mocks/CMSDataDefault'
import errorFunction from 'src/utils/error/errorFunction'
import ImageListSchema from 'src/components/common/ImageListSchema/ImageListSchema'
import Slider from 'react-slick'
import IconArrowLeftAssembly from 'src/images/svg/icon-arrow-left-assemby'
import IconArrowRightAssembly from 'src/images/svg/icon-arrow-right-assembly'
import useWindowDimensions from 'src/hooks/useWindowDimensions'
import { isValidSizes } from 'src/utils/isValidSizes'

import DynamicHeadingTags from '../../common/DynamicHeadingTags/DynamicHeadingTags'
import { formatAlt } from '../../../utils/formatAlt'
import Thumbs from './thumbs'
import './styles.scss'

export type NavigationCategoryProps = {
  src: string
  href: string
  alt?: string
  label?: string
  promotion?: boolean
  campaignEco?: string
  actionType?: string
  categoryClick?: string
  collectionClick?: string
  menuUrl?: string
  pageType?: string
}

type Props = {
  banners: NavigationCategoryProps[]
  id?: string
  dataTestId?: string
  background?: string
  textcolor?: string
  title?: string
  headingTitle: HeadingLevel
  subtitle?: string
  componentLayout?: string
  imageWidth?: string
  imageHeight?: string
  imageLoading: 'lazy' | 'eager' | undefined
}

function NavigationCategory(props: Props) {
  const { isMobile } = useWindowDimensions()

  const DATA = {
    banners: props?.banners ?? DEFAULT_DATA_NAVIGATION_CATEGORY,
    title: props?.title,
    headingTitle: props?.headingTitle,
    subtitle: props?.subtitle,
    componentLayout: props?.componentLayout,
    id: props?.id,
    background: props?.background,
    textcolor: props?.textcolor,
    dataTestId: props?.dataTestId,
    imageLoading: props?.imageLoading ?? 'lazy',
  }

  const {
    banners,
    title,
    headingTitle,
    subtitle,
    componentLayout,
    id,
    background,
    textcolor,
    imageLoading,
  } = DATA

  const styleImage = isValidSizes(props?.imageWidth, props?.imageHeight, null)
  const [style, setStyle] = useState('')
  const { ref, inView } = useInView()
  const firstRender = useRef(false)
  const categoryDataLayer = banners?.map((item) => {
    const { creativeFormat, idFormat, nameFormat, positionFormat } = formatAlt(
      item?.alt ?? ''
    )

    return {
      id: idFormat,
      name: nameFormat,
      creative: creativeFormat,
      position: positionFormat,
    }
  })

  const { sendPromoView } = useEventPromoView(categoryDataLayer)
  const { sendErrorComponentEvent } =
    useErrorComponentEvent('NavigationCategory')

  useEffect(() => {
    setTimeout(() => {
      if (inView && !firstRender.current) {
        sendPromoView()
        firstRender.current = true
      }
    }, 2000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  useEffect(() => {
    if (
      (window?.location?.pathname === '/categoria/life' &&
        componentLayout !== 'vivara') ||
      componentLayout === 'life'
    ) {
      setStyle('life')
    }

    if (!props || !props.banners?.[0].src || !props.banners?.[0].href) {
      errorFunction(props, 'NavigationCategory', sendErrorComponentEvent)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentLayout])

  const categoryClass = 'navigation-category'

  if (!props || !props.banners?.[0].src || !props.banners?.[0].href) {
    return <></>
  }

  const settings = {
    arrows: true,
    dots: false,
    infinite: true,
    slidesToShow: isMobile ? 2 : 6,
    slidesToScroll: 1,
    className: '',
    initialSlide: 0,
    nextArrow: <IconArrowRightAssembly onClick={() => {}} />,
    prevArrow: <IconArrowLeftAssembly onClick={() => {}} />,
    variableWidth: true,
    speed: 500,
  }

  return (
    <>
      <div
        id={id}
        data-testid={props?.dataTestId}
        className={`${categoryClass}-container home-navigationcategory`}
        ref={ref}
        style={{ backgroundColor: background }}
      >
        {
          <>
            <div className={`${categoryClass}-description`}>
              <div className={`${categoryClass}-title ${style}`}>
                <DynamicHeadingTags
                  as={headingTitle}
                  style={{ color: textcolor }}
                >
                  {title}
                </DynamicHeadingTags>
              </div>
              <div
                className={`${categoryClass}-subtitle home-navigationcategory-title ${style}`}
              >
                <p style={{ color: '#737373' }}>{subtitle}</p>
              </div>
              <div className={`${categoryClass}-description-border ${style}`} />
            </div>
            {(!isMobile && banners?.length <= 6) ||
            (isMobile && banners?.length <= 2) ? (
              <div
                className={`${categoryClass}-items home-navigationcategory-items`}
              >
                <div
                  className={`${categoryClass}-images home-navigationcategory-images`}
                >
                  {banners?.map((item) => (
                    <div key={item?.label}>
                      <Thumbs
                        banner={item}
                        promotion={item.promotion}
                        campaignEco={item.campaignEco}
                        actionType={item.actionType}
                        categoryClick={item.categoryClick}
                        collectionClick={item.collectionClick}
                        menuUrl={item.menuUrl}
                        pageType={item.pageType}
                        style={style}
                        textcolor={textcolor}
                        categoryClass={categoryClass}
                        styleImage={styleImage}
                        loading={imageLoading}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`${categoryClass}-thumbs`}>
                <Slider {...settings}>
                  {banners?.map((item) => (
                    <div key={item?.label}>
                      <Thumbs
                        banner={item}
                        promotion={item.promotion}
                        campaignEco={item.campaignEco}
                        actionType={item.actionType}
                        categoryClick={item.categoryClick}
                        collectionClick={item.collectionClick}
                        menuUrl={item.menuUrl}
                        pageType={item.pageType}
                        style={style}
                        textcolor={textcolor}
                        categoryClass={categoryClass}
                        styleImage={styleImage}
                        loading={imageLoading}
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            )}
          </>
        }
      </div>
      <ImageListSchema itemList={banners} name="Navigation Category" />
    </>
  )
}

export default NavigationCategory
